// Phase 6.6 B — auto-discovering GNSS-config accordion filter.
//
// Replaces the hardcoded five-pill row from Phase 6 (gps, glonass,
// galileo, beidou, all-4). The list of available combos comes from
// the new `/analytics/available-gnss-configs` endpoint — so when
// the operator runs an experimental triple like `gps+gal+bds`,
// the filter surfaces it without a code change.
//
// Visual contract (spec §B.2):
//   * Collapsed: «▾ GNSS-СИСТЕМЫ — выбрано: GPS, все 4, GPS+ГЛО»
//   * Expanded:  «▴ GNSS-СИСТЕМЫ — выбрано: …»
//                [✓ GPS] [✓ все 4] [Galileo] [GPS+ГЛО] [GPS+BDS] …
//
// Expand state lives in component-local `useState`. Spec §B.2 calls
// out explicitly that this is in-memory only — the accordion goes
// back to collapsed on every fresh visit.

import { useMemo, useState } from 'react';

import type { AnalyticsAvailableConfig, AnalyticsFilters } from '../../api/types';
import { formatConfig } from './formatConfig';

interface Props {
  /** Active filter state from the parent (AnalyticsScreen). */
  value: AnalyticsFilters;
  /** Emit a full new filter object so the parent can blanket-
   *  invalidate the analytics React Query cache. */
  onChange: (next: AnalyticsFilters) => void;
  /** Available combos from `useAvailableConfigs`. Empty / undefined
   *  during the initial load — the component falls back to the
   *  hardcoded five-pill set so the screen never renders empty. */
  available: AnalyticsAvailableConfig[] | undefined;
}

/** Phase 6 fallback list — five canonical chips used when the
 *  backend list hasn't arrived yet (initial mount or network
 *  failure). Matches the pre-6.6 hardcoded array verbatim so the
 *  graceful-degrade case looks identical to the old UI. */
const FALLBACK_CONFIGS: AnalyticsAvailableConfig[] = [
  { key: 'gps', label: 'GPS', count: 0 },
  { key: 'glonass', label: 'ГЛО', count: 0 },
  { key: 'galileo', label: 'GAL', count: 0 },
  { key: 'beidou', label: 'BDS', count: 0 },
  { key: 'beidou,galileo,glonass,gps', label: 'все 4', count: 0 },
];

export function GnssConfigsFilter({ value, onChange, available }: Props) {
  const [expanded, setExpanded] = useState(false);

  // Use the live backend list if it landed, else the five-pill
  // fallback. Drop entries with count == 0 from the backend list —
  // spec §B.4.5 says these should not be displayed. (The fallback
  // entries have count == 0 by construction; we keep them visible
  // because the fallback path is precisely the «no data yet» state.)
  const displayConfigs: AnalyticsAvailableConfig[] = useMemo(() => {
    if (!available || available.length === 0) return FALLBACK_CONFIGS;
    return available.filter((c) => c.count > 0);
  }, [available]);

  const selected: string[] = value.gnss_configs ?? [];

  const togglePill = (key: string) => {
    const next = selected.includes(key)
      ? selected.filter((c) => c !== key)
      : [...selected, key];
    onChange({
      ...value,
      gnss_configs: next.length > 0 ? next : null,
    });
  };

  // «выбрано: …» summary string. Render the operator-friendly
  // labels (not the raw keys) — pulled from the live backend list
  // when available, otherwise resolved through the cross-screen
  // `formatConfig` helper.
  const labelByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of displayConfigs) map.set(c.key, c.label);
    return map;
  }, [displayConfigs]);

  const selectedLabels = selected
    .map((k) => labelByKey.get(k) ?? formatConfig(k))
    .join(', ');

  return (
    <div className="filter-group gnss-configs-accordion">
      <button
        type="button"
        className="gnss-configs-accordion-header"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid="gnss-configs-accordion-header"
      >
        <span className="gnss-configs-accordion-caret" aria-hidden="true">
          {expanded ? '▴' : '▾'}
        </span>
        <span className="gnss-configs-accordion-label">GNSS-системы</span>
        <span className="gnss-configs-accordion-selected">
          {selected.length > 0 ? `выбрано: ${selectedLabels}` : 'все'}
        </span>
      </button>
      {expanded && (
        <div
          className="config-pills"
          role="group"
          aria-label="Фильтр по GNSS-конфигам"
          data-testid="gnss-configs-pills"
        >
          {displayConfigs.map(({ key, label }) => {
            const active = selected.includes(key);
            return (
              <label
                key={key}
                className={`config-pill ${active ? 'active' : ''}`}
                data-testid={`gnss-config-pill-${key}`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => togglePill(key)}
                  aria-label={`config ${label}`}
                />
                <span className="config-pill-label">{label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
